import { TAP_MAX_MOVE_PX, LONG_PRESS_MS } from "../constants";
import { isInPaneArea } from "./hitRegions";

const MODE = {
  // Touch: not yet a pan, may become a long press
  PENDING: "pending",
  PAN: "pan",
  // Touch: after a long press the crosshair follows the finger
  CROSSHAIR: "crosshair",
};

const distance = (first, second) => Math.hypot(first.x - second.x, first.y - second.y);

/**
 * Drag in the pane: horizontal pan with inertia, prices never move
 * vertically (only the price axis scales them); in the oscillator panes that do not all fit
 * (D24) a vertical drag scrolls them. A finger starts the pan after it moved
 * TAP_MAX_MOVE_PX; held still for LONG_PRESS_MS it shows the crosshair,
 * which follows the finger until it is lifted. The page scrolls under a finger
 * that goes up or down, unless this drag has a use for it (holdsPageScroll).
 *
 * @param {object} view - see createInteraction
 * @param {object} params
 * @param {{ x: number, y: number }} params.point - CSS px of the chart
 * @param {number} params.time - event.timeStamp
 * @param {boolean} params.isTouch
 * @param {boolean} [params.canLongPress] - false for the finger left after a pinch
 * @returns {{ move: Function, end: Function, holdsPageScroll: () => boolean }}
 */
export const createPaneDrag = (view, { point, time, isTouch, canLongPress = true }) => {
  const { timeScale, paneStack, kinetic, overlay } = view;
  const canScrollPanes = Boolean(overlay.layout) && paneStack.canScroll && isInPaneArea(point, overlay.layout);
  let mode = isTouch ? MODE.PENDING : MODE.PAN;
  let lastPoint = point;
  let longPressTimer = null;
  // The finger set off sideways: the pan goes on whichever way it turns
  let isSideways = false;

  if (isTouch && canLongPress) {
    longPressTimer = setTimeout(() => {
      mode = MODE.CROSSHAIR;
      overlay.setPointer(lastPoint);
    }, LONG_PRESS_MS);
  }

  kinetic.begin(point.x, time);

  const pan = (nextPoint, eventTime) => {
    timeScale.scrollBy(nextPoint.x - lastPoint.x);

    if (canScrollPanes) paneStack.scrollBy(lastPoint.y - nextPoint.y);

    kinetic.track(nextPoint.x, eventTime);
    view.invalidate();
  };

  return {
    move: (nextPoint, eventTime) => {
      if (mode === MODE.PENDING) {
        if (distance(nextPoint, point) < TAP_MAX_MOVE_PX) return;

        clearTimeout(longPressTimer);
        mode = MODE.PAN;
        isSideways = Math.abs(nextPoint.x - point.x) >= Math.abs(nextPoint.y - point.y);
      }

      if (mode === MODE.CROSSHAIR) {
        overlay.setPointer(nextPoint);
      } else {
        pan(nextPoint, eventTime);
      }

      lastPoint = nextPoint;
    },

    /** @returns {boolean} true while the finger is needed here and must not scroll the page */
    holdsPageScroll: () => !canLongPress || isSideways || mode === MODE.CROSSHAIR || canScrollPanes,

    /** @param {{ time: number, isCancelled: boolean }} params */
    end: ({ time: endTime, isCancelled }) => {
      clearTimeout(longPressTimer);

      if (mode === MODE.CROSSHAIR) overlay.setPointer(null);
      if (mode === MODE.PAN && !isCancelled) kinetic.release(endTime);
    },
  };
};
